/**
 * Theme-wide events.
 *
 * Everything that changes shared state announces it on `document` rather than
 * calling the components that care. That is what lets the cart bubble, the
 * drawer and the cart page all react to one add-to-cart without knowing about
 * each other, and it is why a new consumer needs no change to the producer.
 */
export const ThemeEvents = {
  /** The cart contents changed. Detail carries the fresh cart JSON. */
  cartUpdate: 'cart:update',
  /** An add-to-cart failed. Detail carries the message Shopify returned. */
  cartError: 'cart:error',
  /** A variant was selected on a product form. */
  variantUpdate: 'variant:update',
  /** A drawer or modal opened or closed. */
  dialogToggle: 'dialog:toggle',
};

/**
 * @typedef {object} CartUpdateDetail
 * @property {object} cart - The cart JSON returned by the Ajax API.
 * @property {string} [source] - Which component performed the change.
 * @property {number} [itemCount] - Convenience copy of `cart.item_count`.
 */

export class CartUpdateEvent extends CustomEvent {
  /** @param {CartUpdateDetail} detail */
  constructor(detail) {
    super(ThemeEvents.cartUpdate, { bubbles: true, detail });
  }
}

export class CartErrorEvent extends CustomEvent {
  /** @param {{ message: string, source?: string }} detail */
  constructor(detail) {
    super(ThemeEvents.cartError, { bubbles: true, detail });
  }
}

export class VariantUpdateEvent extends CustomEvent {
  /** @param {{ variant: object | null, productUrl: string, sectionId: string }} detail */
  constructor(detail) {
    super(ThemeEvents.variantUpdate, { bubbles: true, detail });
  }
}
